import { useState, useEffect } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import axios from 'axios';
import { Wallet as WalletIcon, Plus, ArrowDownRight, ArrowUpRight, Gift, Loader2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShopWalletPage() {
  const { user } = useOutletContext();
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWallet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchWallet = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      if (!token) { setLoading(false); return; }
      const { data } = await axios.get(`${API}/shop/wallet`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBalance(data?.balance || 0);
      setTransactions(data?.transactions || []);
    } catch {
      // No wallet API yet — graceful fallback so the page is still useful
      setBalance(0);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMoney = () => {
    toast.info('Add Money via Razorpay — coming with checkout integration');
  };

  if (!user) {
    return (
      <div className="page-empty" data-testid="wallet-not-logged-in" style={{ padding: 32, textAlign: 'center' }}>
        <WalletIcon className="w-12 h-12 mx-auto text-orange-500 mb-3" />
        <h2 className="text-lg font-bold mb-1">Login to view your DharmShop Wallet</h2>
        <p className="text-sm text-gray-500 mb-4">Track refunds, gift money, and reward points</p>
        <Link to="/auth" className="inline-block px-5 py-2.5 bg-[#FF7A00] text-white font-bold rounded-lg" data-testid="wallet-login-cta">
          Login
        </Link>
      </div>
    );
  }

  return (
    <div className="shop-page" data-testid="wallet-page" style={{ paddingBottom: 80 }}>
      {/* Balance card */}
      <div className="shop-fk-section" style={{ marginTop: 8, padding: 16 }}>
        <div
          className="rounded-xl p-5 text-white"
          style={{ background: 'linear-gradient(135deg, #FF7A00 0%, #E56A00 100%)' }}
          data-testid="wallet-balance-card"
        >
          <div className="text-xs opacity-90 tracking-wider">DHARMSHOP WALLET</div>
          <div className="flex items-end justify-between mt-1">
            <div>
              <div className="text-[11px] opacity-80 mb-1">Available balance</div>
              <div className="text-3xl font-extrabold" data-testid="wallet-balance">
                ₹{loading ? '—' : balance.toLocaleString('en-IN')}
              </div>
            </div>
            <WalletIcon className="w-9 h-9 opacity-30" />
          </div>
          <button
            onClick={handleAddMoney}
            className="mt-4 inline-flex items-center gap-1.5 bg-white text-[#E56A00] font-bold text-sm px-4 py-2 rounded-full"
            data-testid="wallet-add-money-btn"
          >
            <Plus className="w-4 h-4" /> Add Money
          </button>
        </div>
      </div>

      {/* Quick actions */}
      <div className="shop-fk-section" style={{ marginTop: 8 }}>
        <div className="grid grid-cols-3 gap-2">
          <button className="flex flex-col items-center gap-1.5 py-3 bg-orange-50 rounded-lg" data-testid="wallet-action-rewards">
            <Gift className="w-5 h-5 text-orange-600" />
            <span className="text-xs font-semibold text-gray-800">Rewards</span>
          </button>
          <button className="flex flex-col items-center gap-1.5 py-3 bg-green-50 rounded-lg" data-testid="wallet-action-refunds">
            <ArrowDownRight className="w-5 h-5 text-green-600" />
            <span className="text-xs font-semibold text-gray-800">Refunds</span>
          </button>
          <button className="flex flex-col items-center gap-1.5 py-3 bg-blue-50 rounded-lg" data-testid="wallet-action-payments">
            <ArrowUpRight className="w-5 h-5 text-blue-600" />
            <span className="text-xs font-semibold text-gray-800">Payments</span>
          </button>
        </div>
      </div>

      {/* Transactions */}
      <div className="shop-fk-section">
        <h2 className="shop-fk-section-title">Recent Transactions</h2>
        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8" data-testid="wallet-empty-tx">
            <WalletIcon className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No transactions yet</p>
            <p className="text-xs text-gray-400 mt-1">Your wallet activity will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between py-3" data-testid={`wallet-tx-${tx.id}`}>
                <div>
                  <div className="text-sm font-semibold text-gray-800">{tx.title}</div>
                  <div className="text-xs text-gray-500">{new Date(tx.created_at).toLocaleDateString()}</div>
                </div>
                <div className={`text-sm font-bold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-500'}`}>
                  {tx.type === 'credit' ? '+' : '-'}₹{tx.amount}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help */}
      <div className="shop-fk-section">
        <Link to="/shop/menu" className="flex items-center justify-between py-2 text-sm text-gray-600">
          Wallet FAQ & support
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
